/* eslint-disable no-param-reassign */
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { getBaseUrl } from 'devtools/config';
import { Goof } from 'error/goof.interfaces';
import { parseNetworkGoof } from 'error/parse-network-goof';
import { debugLog } from 'utils/debug';
import { isProductionEnvironment } from 'utils/env';

const API_URL = getBaseUrl();
interface CustomConfig extends AxiosRequestConfig {
  dontProcessResults?: boolean;
}

type ErrorHandler = (goof: Goof) => void;
type ErrorHandlers = Record<number, ErrorHandler>;
type RequestConfig = Omit<CustomConfig, 'method' | 'url'>;

const errorHandlers: ErrorHandlers = {
  500: () => {
    if (isProductionEnvironment()) {
      // @TODO: capture datadog exception
      // captureException(goof)
    }
  },
};
export const httpClient = axios.create({
  baseURL: API_URL,
  maxRedirects: 0,
});

function handleError(error: AxiosError) {
  return parseNetworkGoof(error).then((goof: Goof) => {
    let errorHandler: ErrorHandler | null = null;

    if (error.response?.status) {
      errorHandler = errorHandlers[error.response.status];
    }

    debugLog(`/sync/Request.handleError() handling ${error.status} response`);

    if (errorHandler) {
      errorHandler(goof);
    }

    throw goof;
  });
}

function request<T>(config: AxiosRequestConfig) {
  return httpClient
    .request<T>(config)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return handleError(error);
    });
}

let authenticationToken: string | null = null;
// Utility to convert Axios request config to Fetch init config
function convertAxiosConfigToFetchInit(axiosConfig: AxiosRequestConfig): RequestInit {
  // Construct the headers using key-value pairs
  const headers = new Headers();
  if (axiosConfig.headers) {
    Object.entries(axiosConfig.headers).forEach(([key, value]) => {
      if (value !== undefined) {
        // Only set the header if the value is defined
        headers.append(key, value);
      }
    });
  }

  if (authenticationToken) {
    headers.append('Authorization', `${authenticationToken}`);
  }

  const init: RequestInit = {
    method: axiosConfig.method,
    headers,
    credentials: axiosConfig.withCredentials ? 'include' : 'same-origin',
  };

  // Add the signal to the fetch init object if it exists
  if (axiosConfig.signal) {
    init.signal = axiosConfig.signal as NonNullable<RequestInit['signal']>;
  }
  // If there's a request payload, add it to the init object
  if (axiosConfig.data) {
    init.body = JSON.stringify(axiosConfig.data);
    headers.set('Content-Type', 'application/json');
  }

  return init;
}

// New request function using Fetch API
function requestWithFetch<T>(config: AxiosRequestConfig): Promise<T> {
  const fetchConfig = convertAxiosConfigToFetchInit(config);
  const url = `${API_URL}${config.url}`;

  return fetch(url, fetchConfig)
    .then(async (response) => {
      if (!response.ok) {
        // If the HTTP status code is not successful, throw an error
        // const errorResponse = await response.json();
        return Promise.reject(response);
      }

      return Promise.resolve(response) as Promise<T>;
    })
    .catch(async (error) => {
      if (!(error instanceof Response)) {
        return Promise.reject(error);
      }
      const body = await error.json();
      // Here, convert the fetch error to the format expected by handleError
      const axiosError = {
        response: {
          status: error.status,
          data: body,
        },
      } as AxiosError;
      return handleError(axiosError);
    });
}

export function setBaseUrl(baseUrl: string) {
  httpClient.defaults.baseURL = baseUrl;
}

export function setAuthToken(authToken: string | null) {
  authenticationToken = authToken;
  httpClient.defaults.headers.common.Authorization = authToken;
}

export function addErrorHandler(statusCode: number, errHandlerFunc: ErrorHandler) {
  debugLog(`/sync/Request.addHandler() Adding handler for status code '${statusCode}'`);
  Object.assign(errorHandlers, { statusCode: errHandlerFunc });
}

export function getRequest<T>(url: string, config?: RequestConfig) {
  return request<T>({ method: 'GET', url, ...config });
}

export function putRequest<T>(url: string, config?: RequestConfig) {
  return request<T>({ method: 'PUT', url, ...config });
}

export function s3FileUpload(url: string, data: any, config: any) {
  return axios
    .put(url, data, config)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return handleError(error);
    });
}

export function postRequest<T>(url: string, config?: RequestConfig, rawResponseTextInterceptor?: (responseText: string) => string) {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    ...config,
  };

  if (rawResponseTextInterceptor) {
    requestConfig.transformResponse = [
      rawResponseTextInterceptor,
      ...(Array.isArray(axios.defaults.transformResponse)
        ? axios.defaults.transformResponse
        : ([axios.defaults.transformResponse].filter(Boolean) as any)),
    ];
  }

  return request<T>(requestConfig);
}

export function postRequestDontProcessResults<T>(url: string, config?: RequestConfig) {
  const res = requestWithFetch<T>({ method: 'POST', url, ...config });
  return res;
}

export function patchRequest<T>(url: string, config?: RequestConfig) {
  return request<T>({ method: 'PATCH', url, ...config });
}

export function deleteRequest<T>(url: string, config?: RequestConfig) {
  return request<T>({ method: 'DELETE', url, ...config });
}
